import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { LearningLanguage, Verb, VerbsData } from '../types';
import { t } from '../i18n/translations';
import type { Language } from '../i18n/translations';
import SwipeCard from './SwipeCard';
import SwipeToolbar from './SwipeToolbar';
import { getVerbBadgeLabel, getVerbDeckTitle, getVerbPracticeGroupIds } from '../utils/verbPractice';
import './KoniugacjaPage.css';

type TaggedVerb = Verb & { konj: string };

interface KoniugacjaPageProps {
  verbsData: VerbsData;
  onBack: () => void;
  onGenerateSingle?: (verbInf: string) => void;
  onNavigateToSettings?: () => void;
  onNavigateToChat?: () => void;
  initialVerb?: { group: string; verb: string };
  learningLanguage?: LearningLanguage;
  language?: Language;
}

function buildDeck(
  verbsData: VerbsData,
  group: string,
  learningLanguage: LearningLanguage,
): TaggedVerb[] {
  const groups = getVerbPracticeGroupIds(verbsData, learningLanguage);
  if (group === 'all') {
    return groups.flatMap((g) =>
      (verbsData[g] || []).map((v) => ({ ...v, konj: getVerbBadgeLabel(g, learningLanguage) })),
    );
  }
  return (verbsData[group] || []).map((v) => ({ ...v, konj: getVerbBadgeLabel(group, learningLanguage) }));
}

function parseInitialDeckAndIndex(
  verbsData: VerbsData,
  learningLanguage: LearningLanguage,
): { deck: string; index: number } {
  const groupIds = getVerbPracticeGroupIds(verbsData, learningLanguage);
  const params = new URLSearchParams(window.location.search);
  const group = params.get('group');
  const verb = params.get('verb');
  if (group && groupIds.includes(group) && verb) {
    const verbs = verbsData[group] || [];
    const idx = verbs.findIndex((v) => v.inf === verb);
    if (idx >= 0) return { deck: group, index: idx };
  }
  return { deck: groupIds[0] || '1', index: 0 };
}

export default function KoniugacjaPage({
  verbsData,
  onBack,
  onGenerateSingle,
  onNavigateToSettings,
  onNavigateToChat,
  initialVerb,
  learningLanguage = 'pl',
  language = 'en',
}: KoniugacjaPageProps) {
  const [initial] = useState(() => {
    if (initialVerb) {
      const verbs = verbsData[initialVerb.group] || [];
      const idx = verbs.findIndex((v) => v.inf === initialVerb.verb);
      if (idx >= 0) return { deck: initialVerb.group, index: idx };
    }
    return parseInitialDeckAndIndex(verbsData, learningLanguage);
  });
  const [deck, setDeck] = useState(initial.deck);
  const [wordLang, setWordLang] = useState<'pl' | 'ru'>('pl');
  const [hideTables, setHideTables] = useState(false);
  const [hideTranslation, setHideTranslation] = useState(false);
  const [randomMode, setRandomMode] = useState(false);
  const [showKonj, setShowKonj] = useState(true);
  const [formVisibility, setFormVisibility] = useState<Record<string, boolean>>({});
  const [cardIndex, setCardIndex] = useState(initial.index);

  const stageRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);
  const touchRef = useRef<{ startX: number; startY: number; tracking: boolean }>({
    startX: 0,
    startY: 0,
    tracking: false,
  });

  const isAllDeck = deck === 'all';

  const cards = useMemo(() => {
    return buildDeck(verbsData, deck, learningLanguage);
  }, [verbsData, deck, learningLanguage]);

  const clampedIndex = cards.length === 0 ? 0 : Math.max(0, Math.min(cardIndex, cards.length - 1));

  useEffect(() => {
    const dotsEl = dotsRef.current;
    if (!dotsEl) return;
    const activeDot = dotsEl.children[clampedIndex] as HTMLElement | undefined;
    if (activeDot?.scrollIntoView) {
      activeDot.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [clampedIndex]);

  const randomOther = useCallback(() => {
    if (cards.length <= 1) return;
    setCardIndex((prev) => {
      let r: number;
      do {
        r = Math.floor(Math.random() * cards.length);
      } while (r === prev);
      return r;
    });
  }, [cards.length]);

  const next = useCallback(() => {
    if (cards.length === 0) return;
    if (randomMode) {
      randomOther();
      return;
    }
    setCardIndex((prev) => (prev + 1) % cards.length);
  }, [randomMode, randomOther, cards.length]);

  const prev = useCallback(() => {
    if (cards.length === 0) return;
    if (randomMode) {
      randomOther();
      return;
    }
    setCardIndex((p) => (p - 1 + cards.length) % cards.length);
  }, [randomMode, randomOther, cards.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    touchRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      tracking: true,
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchRef.current.tracking) return;
      touchRef.current.tracking = false;
      const touch = e.changedTouches?.[0];
      if (!touch) return;
      const dx = touch.clientX - touchRef.current.startX;
      const dy = touch.clientY - touchRef.current.startY;
      if (Math.abs(dy) > Math.abs(dx)) return;
      const threshold = 40;
      if (dx < -threshold) next();
      if (dx > threshold) prev();
    },
    [next, prev],
  );

  const handleStageClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('input, label, button, select, a')) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x > rect.width / 2) next();
      else prev();
    },
    [next, prev],
  );

  const handleFormToggle = useCallback((key: string, shown: boolean) => {
    setFormVisibility((prev) => ({ ...prev, [key]: shown }));
  }, []);

  const handleDeckChange = useCallback((newDeck: string) => {
    setDeck(newDeck);
    setCardIndex(0);
  }, []);

  const handleRandomModeChange = useCallback((on: boolean) => {
    setRandomMode(on);
  }, []);

  const handleDotClick = useCallback(
    (idx: number) => {
      setCardIndex(Math.max(0, Math.min(idx, cards.length - 1)));
    },
    [cards.length],
  );

  const effectiveShowKonj = isAllDeck ? showKonj : false;

  const wrapperClasses = [
    'koniugacja-page',
    hideTables ? 'hide-tables' : '',
    hideTranslation ? 'hide-translation' : '',
    !effectiveShowKonj ? 'hide-konj' : '',
    randomMode ? 'random-mode' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const title = getVerbDeckTitle(deck, learningLanguage);

  return (
    <div className={wrapperClasses}>
      <header>
        <div>
          <div className="title">{title}</div>
          <div className="subtitle">
            {t(language, 'kon.subtitle')}
          </div>
        </div>
        <div className="counter">
          {cards.length === 0 ? '0 / 0' : `${clampedIndex + 1} / ${cards.length}`}
        </div>
      </header>

      <SwipeToolbar
        deck={deck}
        wordLang={wordLang}
        hideTables={hideTables}
        hideTranslation={hideTranslation}
        randomMode={randomMode}
        showKonj={showKonj}
        isAllDeck={isAllDeck}
        onDeckChange={handleDeckChange}
        onWordLangChange={setWordLang}
        onHideTablesChange={setHideTables}
        onHideTranslationChange={setHideTranslation}
        onRandomModeChange={handleRandomModeChange}
        onShowKonjChange={setShowKonj}
        learningLanguage={learningLanguage}
        verbsData={verbsData}
        language={language}
      />

      <div
        className="stage"
        ref={stageRef}
        onClick={handleStageClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="track"
          style={{ transform: `translateX(-${clampedIndex * 100}%)` }}
        >
          {cards.map((verb, idx) => (
            <SwipeCard
              key={`${deck}-${verb.inf}-${idx}`}
              verb={verb}
              wordLang={wordLang}
              cardIndex={idx}
              formVisibility={formVisibility}
              onFormToggle={handleFormToggle}
              learningLanguage={learningLanguage}
              language={language}
            />
          ))}
        </div>
      </div>

      <div className="dots" ref={dotsRef}>
        {cards.map((_, idx) => (
          <div
            key={idx}
            className={`dot${idx === clampedIndex ? ' active' : ''}`}
            onClick={() => handleDotClick(idx)}
          />
        ))}
      </div>

      <div className="controls">
        <div className="btns">
          <button onClick={prev} aria-label={t(language, 'kon.prevCard')}>{t(language, 'kon.prevCard')}</button>
          <button onClick={next} aria-label={t(language, 'kon.nextCard')}>{t(language, 'kon.nextCard')}</button>
        </div>
        {onGenerateSingle && cards.length > 0 && (
          <button
            className="btn-generate-card"
            aria-label={t(language, 'word.generateForVerb')}
            onClick={() => onGenerateSingle(cards[clampedIndex].inf)}
          >
            ✨
          </button>
        )}
        <div className="hint">{t(language, 'kon.hint')}</div>
      </div>

      <div className="back-link">
        <button type="button" onClick={onBack}>{t(language, 'kon.backToMain')}</button>
        {onNavigateToSettings && (
          <button type="button" onClick={onNavigateToSettings}>{t(language, 'nav.settings')}</button>
        )}
        {onNavigateToChat && (
          <button type="button" onClick={onNavigateToChat}>{t(language, 'nav.chat')}</button>
        )}
      </div>
    </div>
  );
}
